import { Box, SimpleGrid, Heading, Text, useColorModeValue, HStack, Button, Link, Badge } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import HorizontalScroller from '../ui/HorizontalScroller';
import { getClothing, ClothingItem } from '../../services/clothing';
import { Link as RouterLink } from 'react-router-dom';

const MerchSection: React.FC<{ variant?: 'grid' | 'carousel' | 'featured' | 'list' }> = ({ variant = 'grid' }) => {
  const [items, setItems] = useState<ClothingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const cardBg = useColorModeValue('white', 'gray.800');
  const eshopUrl = process.env.REACT_APP_ESHOP_URL;

  useEffect(() => {
    const fetchItems = async () => {
      try {
        const data = await getClothing();
        // Show only 5 newest items on homepage
        setItems(data.slice(0, 5));
      } catch (e) {
        console.error('Failed to fetch clothing items:', e);
      } finally {
        setLoading(false);
      }
    };

    fetchItems();
  }, []);

  if (loading || items.length === 0) return null;

  // Carousel variant: horizontally scrollable cards
  if (variant === 'carousel') {
    return (
      <Box>
        <HStack justify="space-between" mb={3}>
          <Heading as="h3" size="md">Oblečení týmu</Heading>
          <HStack spacing={2}>
            <Link as={RouterLink} to="/obleceni">
              <Button size="sm" variant="outline" colorScheme="blue">Zobrazit vše</Button>
            </Link>
            {eshopUrl && (
              <Button
                as={Link}
                href={eshopUrl}
                size="sm"
                colorScheme="blue"
                variant="solid"
                isExternal
              >
                E-shop
              </Button>
            )}
          </HStack>
        </HStack>
        <HorizontalScroller draggable>
          {items.map((it) => (
            <Box key={it.id} minW={{ base: '70%', md: '45%', lg: '28%' }}>
              <a 
                href={it.url || '/obleceni'} 
                target={it.url ? "_blank" : undefined}
                rel={it.url ? "noreferrer noopener" : undefined}
              >
                <Box 
                  className="card"
                  bg={cardBg} 
                  overflow="hidden" 
                  borderWidth="1px"
                  transition="all 0.2s"
                  _hover={{ transform: 'translateY(-4px)', boxShadow: 'md' }}
                >
                  <Box
                    aria-hidden
                    height={{ base: 140, md: 180 }}
                    bgSize="cover"
                    bgPos="center"
                    style={{ backgroundImage: `url(${it.image_url})` }}
                  />
                  <Box p={3} borderTopWidth="1px">
                    <Text noOfLines={1} fontWeight="semibold" fontSize="sm">{it.title}</Text>
                    {it.price && it.price > 0 && (
                      <Badge colorScheme="blue" mt={1} fontSize="xs">
                        {it.price} {it.currency || 'Kč'}
                      </Badge>
                    )}
                  </Box>
                </Box>
              </a>
            </Box>
          ))}
        </HorizontalScroller>
      </Box>
    );
  }

  // Grid/list (default) variant
  return (
    <Box>
      <HStack justify="space-between" mb={3}>
        <Heading as="h3" size="md">Oblečení týmu</Heading>
        <HStack spacing={2}>
          <Link as={RouterLink} to="/obleceni">
            <Button size="sm" variant="outline" colorScheme="blue">Zobrazit vše</Button>
          </Link>
          {eshopUrl && (
            <Button
              as={Link}
              href={eshopUrl}
              size="sm"
              colorScheme="blue"
              variant="solid"
              isExternal
            >
              E-shop
            </Button>
          )}
        </HStack>
      </HStack>
      <SimpleGrid columns={{ base: 2, md: 3, lg: 5 }} spacing={4}>
        {items.map((it) => (
          <a 
            key={it.id} 
            href={it.url || '/obleceni'} 
            target={it.url ? "_blank" : undefined}
            rel={it.url ? "noreferrer noopener" : undefined}
          >
            <Box 
              className="card"
              bg={cardBg} 
              overflow="hidden" 
              borderWidth="1px"
              transition="all 0.2s"
              _hover={{ transform: 'translateY(-4px)', boxShadow: 'md' }}
            >
              <Box
                aria-hidden
                height={{ base: 140, md: 180 }}
                bgSize="cover"
                bgPos="center"
                style={{ backgroundImage: `url(${it.image_url})` }}
              />
              <Box p={3} borderTopWidth="1px">
                <Text noOfLines={1} fontWeight="semibold" fontSize="sm">{it.title}</Text>
                {it.price && it.price > 0 && (
                  <Badge colorScheme="blue" mt={1} fontSize="xs">
                    {it.price} {it.currency || 'Kč'}
                  </Badge>
                )}
              </Box>
            </Box>
          </a>
        ))}
      </SimpleGrid>
    </Box>
  );
};

export default MerchSection;
